/**
 * Shared state primitives for optional cart drawer enhancements.
 */
(function initializeCartUpsellState(global) {
  const UPSELL_OWNER_PROPERTY = '_cart_upsell_owner';

  /**
   * Resolve one Shopify cart line by its exact variant id.
   *
   * @param {{items?: Array<object>} | null} cart
   * @param {string | number} variantId
   * @param {string} preferredLineKey
   * @returns {object | null}
   */
  function resolveUpsellLine(cart, variantId, preferredLineKey = '') {
    if (!Array.isArray(cart?.items)) return null;
    const preferredLine = cart.items.find((item) => (
      String(item.key) === String(preferredLineKey) &&
      String(item.variant_id) === String(variantId)
    ));
    if (preferredLine) return preferredLine;
    return cart.items.find((item) => String(item.variant_id) === String(variantId)) || null;
  }

  /**
   * Resolve a line only when Shopify returned the private owner property added
   * by this exact upsell card. A matching variant or key without ownership is
   * customer cart state and must never be removed by the upsell.
   *
   * @param {{items?: Array<object>} | null} cart
   * @param {{ownerKey: string, preferredLineKey?: string, variantId?: string | number}} input
   * @returns {object | null}
   */
  function resolveOwnedUpsellLine(
    cart,
    { ownerKey, preferredLineKey = '', variantId = '' },
  ) {
    if (!Array.isArray(cart?.items) || !ownerKey) return null;
    const isOwnedVariant = (item) => (
      String(item?.properties?.[UPSELL_OWNER_PROPERTY] || '') === String(ownerKey) &&
      (!variantId || String(item.variant_id) === String(variantId))
    );
    const preferredLine = cart.items.find((item) => (
      String(item.key) === String(preferredLineKey) && isOwnedVariant(item)
    ));

    return preferredLine || cart.items.find(isOwnedVariant) || null;
  }

  /**
   * @param {{items?: Array<object>} | null} cart
   * @param {Set<string> | Array<string>} ownerKeys
   * @returns {Array<object>}
   */
  function findOwnedUpsellLines(cart, ownerKeys) {
    if (!Array.isArray(cart?.items)) return [];
    const owners = new Set(Array.from(ownerKeys || [], (owner) => String(owner)));
    if (owners.size === 0) return [];

    return cart.items.filter((item) => owners.has(
      String(item?.properties?.[UPSELL_OWNER_PROPERTY] || ''),
    ));
  }

  /**
   * Resolve display data exclusively from Shopify's variant and cart-line
   * prices. A cart line wins because its final price includes allocations.
   *
   * @param {{variant: object, line?: object | null}} input
   * @returns {{finalPrice: number, comparePrice: number, discountAllocations: Array<object>}}
   */
  function resolveUpsellPricing({ variant, line = null }) {
    const finalPrice = line?.final_price ?? variant?.price ?? 0;
    const originalPrice = line?.original_price ?? variant?.price ?? 0;
    const variantComparePrice = variant?.compare_at_price ?? 0;
    const comparePrice = originalPrice > finalPrice
      ? originalPrice
      : (variantComparePrice > (variant?.price ?? 0) ? variantComparePrice : 0);
    const discountAllocations = line?.discount_allocations || line?.line_level_discount_allocations || [];

    return { finalPrice, comparePrice, discountAllocations };
  }

  /**
   * Build a Shopify cart/change payload from a resolved line. Line keys remain
   * authoritative when one variant appears in multiple property/plan lines.
   *
   * @param {{line: object, quantity: number, sections?: Array<string>, sectionsUrl?: string}} input
   * @returns {object}
   */
  function createLineChangePayload({ line, quantity, sections = [], sectionsUrl = '' }) {
    if (!line?.key) throw new TypeError('A Shopify cart line key is required.');

    return {
      id: String(line.key),
      quantity: Math.max(0, Number.parseInt(quantity, 10) || 0),
      sections,
      sections_url: sectionsUrl,
    };
  }

  /**
   * @param {number} responseVersion
   * @param {number} currentVersion
   * @returns {boolean}
   */
  function shouldApplyRender(responseVersion, currentVersion) {
    return Number(responseVersion) >= Number(currentVersion);
  }

  /**
   * Require every requested active section to have server-returned HTML before
   * applying any of them, preventing a partially refreshed cart surface.
   *
   * @param {Record<string, string | null> | null} sections
   * @param {Array<string>} sectionIds
   */
  function validateReturnedSections(sections, sectionIds) {
    const missingSections = sectionIds.filter((sectionId) => (
      typeof sections?.[sectionId] !== 'string'
    ));
    if (missingSections.length > 0) {
      throw new Error(`Shopify did not return section HTML for: ${missingSections.join(', ')}.`);
    }
  }

  /**
   * Own the AbortController for one custom-element connection. Reconnecting
   * first aborts the previous connection and all listeners attached to it.
   *
   * @param {() => AbortController} createController
   */
  function createConnectionLifecycle(createController = () => new AbortController()) {
    let controller = null;

    return {
      connect() {
        controller?.abort();
        controller = createController();
        return controller;
      },
      disconnect() {
        controller?.abort();
        controller = null;
      },
      get signal() {
        return controller?.signal || null;
      },
    };
  }

  /**
   * Serialize cart mutations. A rejected mutation rejects its task, while a
   * later repaint or publication failure is reported without pretending the
   * already-committed server mutation failed.
   */
  function createCartMutationQueue() {
    let tail = Promise.resolve();
    let responseVersion = 0;
    let renderedVersion = 0;

    function enqueue({ mutate, reconcile, repaint, publish }) {
      if (typeof mutate !== 'function') {
        return Promise.reject(new TypeError('A cart mutation function is required.'));
      }

      const version = ++responseVersion;
      const run = async () => {
        const mutationResponse = await mutate(version);
        let response = mutationResponse;
        let reconciliationError = null;
        let rendered = false;
        let repaintError = null;
        let publishError = null;

        if (typeof reconcile === 'function') {
          try {
            response = await reconcile(mutationResponse, version);
          } catch (error) {
            reconciliationError = error;
          }
        }

        if (
          !reconciliationError &&
          typeof repaint === 'function' &&
          shouldApplyRender(version, renderedVersion)
        ) {
          try {
            await repaint(response, version);
            renderedVersion = version;
            rendered = true;
          } catch (error) {
            repaintError = error;
          }
        }

        if (typeof publish === 'function') {
          try {
            await publish(response, {
              version,
              rendered,
              reconciliationError,
              repaintError,
            });
          } catch (error) {
            publishError = error;
          }
        }

        return {
          mutationCommitted: true,
          mutationResponse,
          response,
          responseVersion: version,
          rendered,
          reconciliationError,
          repaintError,
          publishError,
        };
      };

      const result = tail.then(run);
      tail = result.catch(() => undefined);
      return result;
    }

    return {
      enqueue,
      get responseVersion() {
        return responseVersion;
      },
      get renderedVersion() {
        return renderedVersion;
      },
    };
  }

  let sharedCartMutationQueue = null;
  function getSharedCartMutationQueue() {
    sharedCartMutationQueue ||= createCartMutationQueue();
    return sharedCartMutationQueue;
  }

  const api = {
    UPSELL_OWNER_PROPERTY,
    createCartMutationQueue,
    createConnectionLifecycle,
    createLineChangePayload,
    findOwnedUpsellLines,
    getSharedCartMutationQueue,
    resolveOwnedUpsellLine,
    resolveUpsellLine,
    resolveUpsellPricing,
    shouldApplyRender,
    validateReturnedSections,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global) global.CartUpsellState = api;
})(typeof window !== 'undefined' ? window : globalThis);
